import React, { useState } from 'react'
import { Button } from '@components/inputs/Button'
import ConstituentPicker from '@components/event/ConstitutenPicker'

export interface Attendee {
  fullName: string
  email: string
  metadata?: any
}

interface Constituent {
  fullName: string
  email: string
}

interface BlackBaudConstituentsProps {
  localAttendees: Attendee[]
  remoteConstituents: Constituent[]
  isVisible: boolean
  onSave: (
    updatedConstituents: {
      localEmail: string
      selectedEmail: string
      cId?: string
    }[]
  ) => void
  onBack: () => void
}

const BlackBaudConstituents: React.FC<BlackBaudConstituentsProps> = ({
  localAttendees,
  remoteConstituents,
  isVisible,
  onSave,
  onBack,
}) => {
  const [selectedConstituents, setSelectedConstituents] = useState<{
    [key: string]: { email: string; cId?: string }
  }>({})
  const [approvedAttendees, setApprovedAttendees] = useState<Set<string>>(
    new Set()
  )

  const [blacklistAttendees, setBlacklistAttendees] = useState<Set<string>>(
    new Set()
  )
  const [visibleAttendees, setVisibleAttendees] = useState(localAttendees)

  // constitutent picker realted states
  const [showCp, setShowCp] = useState(false)
  const [cpAttendee, setCpAttendee] = useState<Attendee>()

  // Helper function to check partial name or email match
  const matchesConstituent = (attendee: Attendee, constituent: Constituent) => {
    const [attendeeFirstName, attendeeLastName] = attendee.fullName
      .toLowerCase()
      .split(' ')
    const [constituentFirstName, constituentLastName] = constituent.fullName
      .toLowerCase()
      .split(' ')

    // Check if first name, last name or email matches
    const nameMatch =
      (attendeeFirstName &&
        constituent.fullName.toLowerCase().includes(attendeeFirstName)) ||
      (attendeeLastName &&
        constituent.fullName.toLowerCase().includes(attendeeLastName))

    const emailMatch =
      constituent.email.toLowerCase() === attendee.email.toLowerCase()

    return nameMatch || emailMatch
  }

  // Filtered list of remote constituents based on partial matching
  const getMatchingConstituents = (attendee: Attendee) => {
    return remoteConstituents.filter((constituent) =>
      matchesConstituent(attendee, constituent)
    )
  }

  const handleMatch = (selectedValue: { email: string; cId?: string }) => {
    const attendeeEmail = cpAttendee?.email ?? ''
    setShowCp(false)
    setSelectedConstituents({
      ...selectedConstituents,
      [attendeeEmail]: selectedValue,
    })
  }

  const handleApprove = (attendeeEmail: string) => {
    if (selectedConstituents[attendeeEmail]) {
      setApprovedAttendees((prevApprovedAttendees) => {
        const updatedSet = new Set(prevApprovedAttendees)
        updatedSet.add(attendeeEmail)
        return updatedSet
      })
    }
  }

  const handleReject = (attendeeEmail: string) => {
    setVisibleAttendees(
      visibleAttendees.filter((attendee) => attendee.email !== attendeeEmail)
    )
  }

  const handleSave = () => {
    const curatedList = visibleAttendees
      .filter((attendee) => approvedAttendees.has(attendee.email)) // Only return approved attendees
      .map((attendee) => ({
        localEmail: attendee.email,
        selectedEmail: selectedConstituents[attendee.email].email, // Return the pairing of local and selected remote email
        cId: selectedConstituents[attendee.email].cId,
      }))
    onSave(curatedList)
  }

  const hasKeys = (obj: any) =>
    obj && !Array.isArray(obj) && Object.keys(obj).length

  if (!isVisible) return null

  return (
    <div className="p-4">
      <div className="overflow-x-auto">
        <table className="table-auto w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="px-4 py-2 text-left text-lg">Full Name</th>
              <th className="px-4 py-2 text-left text-lg">Email</th>
              <th className="px-4 py-2 text-left text-lg">Participant Data</th>
              <th className="px-4 py-2 text-left text-lg min-w-[200px]">
                Constituent
              </th>
              <th className="px-4 py-2 text-left text-lg">Action</th>
            </tr>
          </thead>
          <tbody>
            {visibleAttendees.map((attendee) => {
              let doNotSync = false
              return (
                <tr
                  key={attendee.email}
                  className="h-20 border-b border-slate-200"
                >
                  <td className="px-4 py-2 text-base">{attendee.fullName}</td>
                  <td className="px-4 py-2 text-base">{attendee.email}</td>
                  <td className="px-4 py-2 text-xs">
                    {hasKeys(attendee?.metadata) ? (
                      <div className="text-sm">
                        {Object.entries(attendee.metadata).map(
                          ([key, value]) => (
                            <div
                              className="whitespace-pre-wrap text-xs"
                              key={key}
                            >
                              <strong>{key}:</strong>{' '}
                              {value && typeof value === 'object' ? (
                                <pre className="whitespace-pre-wrap text-xs">
                                  {JSON.stringify(value, null, 2)}
                                </pre>
                              ) : (
                                String(value)
                              )}
                            </div>
                          )
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-500">No Data</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-base">
                    {approvedAttendees.has(attendee.email) ? (
                      <span className="flex items-center">
                        {selectedConstituents[attendee.email]?.email}

                        <span className="flex items-center ml-4">
                          <Button
                            title={'Undo'}
                            type="secondary"
                            size="small"
                            onClick={() => {
                              setApprovedAttendees((prevApprovedlist) => {
                                const updatedSet = new Set(prevApprovedlist)
                                updatedSet.delete(attendee.email)
                                setSelectedConstituents((state) => {
                                  if (state[attendee.email]) {
                                    const data = Object.entries(state).filter(
                                      (f) => f[0] !== attendee.email
                                    )
                                    const payload = Object.fromEntries(data)
                                    console.log({ payload })
                                    return {
                                      ...payload,
                                    }
                                  }
                                  return state
                                })
                                return updatedSet
                              })
                            }}
                          />
                        </span>
                      </span>
                    ) : blacklistAttendees.has(attendee.email) ? (
                      <span className="flex items-center">
                        <Button
                          title={'Undo'}
                          type="secondary"
                          size="small"
                          onClick={() => {
                            setBlacklistAttendees((prevBlacklist) => {
                              const updatedSet = new Set(prevBlacklist)
                              updatedSet.delete(attendee.email)
                              return updatedSet
                            })
                          }}
                        />
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <Button
                          className="mr-4"
                          title={
                            selectedConstituents?.[attendee.email]?.email
                              ? `${selectedConstituents?.[attendee.email]?.email}`
                              : 'Match'
                          }
                          type="primary"
                          size="small"
                          onClick={() => {
                            setShowCp(true)
                            setCpAttendee(attendee)
                          }}
                        />

                        <Button
                          title={'Do not sync'}
                          type="secondary"
                          size="small"
                          onClick={() => {
                            setBlacklistAttendees((prevBlacklist) => {
                              const updatedSet = new Set(prevBlacklist)
                              updatedSet.add(attendee.email)
                              return updatedSet
                            })
                          }}
                        />
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {/*!approvedAttendees.has(attendee.email) && (
                    <div className="flex space-x-2">
                      <Button
                        title="Approve"
                        type="primary"
                        size="small"
                        textColor="white"
                        onClick={() => handleApprove(attendee.email)}
                        disabled={!selectedConstituents[attendee.email]} // Disable if no option is selected
                      />
                      <Button
                        title="Reject"
                        type="danger"
                        size="small"
                        onClick={() => handleReject(attendee.email)}
                      />
                    </div>
                  )*/}
                    {!approvedAttendees.has(attendee.email) ? (
                      <div className="flex space-x-2">
                        <span className="text-black text-xs">
                          {blacklistAttendees.has(attendee.email)
                            ? "Won't Sync"
                            : 'Awaiting for selection'}
                        </span>
                      </div>
                    ) : (
                      <div className="flex space-x-2">
                        <span className="text-green-600 text-xs">
                          {selectedConstituents?.[attendee.email].email ===
                          'new'
                            ? 'Will be synced to Participant'
                            : 'Will be saved as Constitutent'}
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end mt-4">
        <Button title="Go Back" type="primary" size="small" onClick={onBack} />
        <Button
          title="Update"
          type="blackbaud"
          size="small"
          onClick={handleSave}
          className="ml-2"
        />
      </div>
      <ConstituentPicker
        show={showCp}
        setShow={() => setShowCp(false)}
        name={cpAttendee?.fullName ?? ''}
        email={cpAttendee?.email ?? ''}
        fullAttendee={cpAttendee}
        onMatch={(cntData, attendeeEmail) => {
          // console.log({ cntData })
          handleMatch(cntData)
          setTimeout(() => {
            setApprovedAttendees((prevApprovedAttendees) => {
              const updatedSet = new Set(prevApprovedAttendees)
              updatedSet.add(attendeeEmail)
              return updatedSet
            })
          }, 0)
        }}
        options={
          cpAttendee
            ? getMatchingConstituents(cpAttendee).filter(
                (constituent) =>
                  !Object.values(selectedConstituents)
                    .map((c) => c.email)
                    .includes(constituent.email) ||
                  selectedConstituents[cpAttendee.email].email ===
                    constituent.email
              )
            : []
        }
      />
    </div>
  )
}

export const parseAttendees = (attendeesString: string): Attendee[] => {
  if (!attendeesString) return []

  return attendeesString.split(',').map((attendee) => {
    const [fullName, email] = attendee.trim().split(' (')
    return {
      fullName: fullName.trim(),
      email: email?.replace(')', '').trim() || '',
    }
  })
}

export default BlackBaudConstituents
